import { Mark, mergeAttributes } from "@tiptap/core";
import type { SuggestionHighlightAttrs } from "../types";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    suggestionHighlight: {
      /**
       * Apply a suggestion highlight mark to a specific range
       */
      setSuggestionHighlight: (
        attrs: SuggestionHighlightAttrs,
        from: number,
        to: number,
      ) => ReturnType;
      /**
       * Remove a specific suggestion highlight by ID
       */
      unsetSuggestionHighlight: (suggestionId: string) => ReturnType;
      /**
       * Accept a suggestion: delete the highlighted text and remove the mark.
       * Finds the suggestion by ID anywhere in the document.
       */
      acceptSuggestion: (suggestionId: string) => ReturnType;
      /**
       * Reject a suggestion: remove the mark without deleting text.
       * Alias for unsetSuggestionHighlight for semantic clarity.
       */
      rejectSuggestion: (suggestionId: string) => ReturnType;
    };
  }
}

/**
 * TipTap Mark extension for highlighting AI-suggested text deletions.
 *
 * This mark is used to visually indicate text ranges that the AI suggests
 * should be deleted. Key features:
 *
 * - Stores all suggestion attributes (see SuggestionHighlightAttrs in types.ts)
 * - Renders as a styled span with data attributes for click handling
 * - Does NOT serialize to markdown (ephemeral visual state only)
 * - inclusive: false prevents the mark from extending when user types at boundaries
 *
 * Usage:
 * ```typescript
 * editor.commands.setSuggestionHighlight(
 *   {
 *     suggestionId: "123",
 *     isValid: true,
 *     reason: "DUPLICATE_PRIOR_SEGMENTS",
 *     reasonDetails: "This text appears earlier in the document",
 *     confidence: 0.95,
 *   },
 *   10,  // from position
 *   25   // to position
 * );
 * ```
 */
export const SuggestionHighlightMark = Mark.create({
  name: "suggestionHighlight",

  // Don't extend the mark when typing at boundaries
  inclusive: false,

  // Don't keep the mark when splitting nodes
  keepOnSplit: false,

  // Allow multiple suggestion highlights to overlap
  excludes: "",

  addAttributes() {
    return {
      suggestionId: {
        default: null,
        parseHTML: (element: HTMLElement) =>
          element.getAttribute("data-suggestion-id"),
        renderHTML: (attributes: SuggestionHighlightAttrs) => ({
          "data-suggestion-id": attributes.suggestionId,
        }),
      },
      isValid: {
        default: true,
        parseHTML: (element: HTMLElement) =>
          element.getAttribute("data-is-valid") !== "false",
        renderHTML: (attributes: SuggestionHighlightAttrs) => ({
          "data-is-valid": String(attributes.isValid),
        }),
      },
      reason: {
        default: "",
        parseHTML: (element: HTMLElement) =>
          element.getAttribute("data-reason") || "",
        renderHTML: (attributes: SuggestionHighlightAttrs) => ({
          "data-reason": attributes.reason,
        }),
      },
      reasonDetails: {
        default: undefined,
        parseHTML: (element: HTMLElement) =>
          element.getAttribute("data-reason-details") || undefined,
        renderHTML: (attributes: SuggestionHighlightAttrs) =>
          attributes.reasonDetails
            ? { "data-reason-details": attributes.reasonDetails }
            : {},
      },
      relatedSegmentID: {
        default: null,
        parseHTML: (element: HTMLElement) =>
          element.getAttribute("data-related-segment-id"),
        renderHTML: (attributes: SuggestionHighlightAttrs) =>
          attributes.relatedSegmentID
            ? { "data-related-segment-id": attributes.relatedSegmentID }
            : {},
      },
      confidence: {
        default: undefined,
        parseHTML: (element: HTMLElement) => {
          const value = element.getAttribute("data-confidence");
          return value ? parseFloat(value) : undefined;
        },
        renderHTML: (attributes: SuggestionHighlightAttrs) =>
          attributes.confidence !== undefined
            ? { "data-confidence": String(attributes.confidence) }
            : {},
      },
      textRange: {
        default: undefined,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "span[data-suggestion-id]",
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const isValid = HTMLAttributes["data-is-valid"] !== "false";
    const className = isValid
      ? "suggestion-highlight"
      : "suggestion-highlight suggestion-highlight--invalid";

    return [
      "span",
      mergeAttributes(HTMLAttributes, {
        class: className,
      }),
      0,
    ];
  },

  addCommands() {
    return {
      setSuggestionHighlight:
        (attrs: SuggestionHighlightAttrs, from: number, to: number) =>
        ({ chain }) => {
          return chain()
            .setTextSelection({ from, to })
            .setMark(this.name, attrs)
            .setTextSelection(to) // Move cursor to end to avoid selection
            .run();
        },

      unsetSuggestionHighlight:
        (suggestionId: string) =>
        ({ tr, state, dispatch }) => {
          const { doc } = state;
          let found = false;

          // Find and remove marks with the specific suggestionId
          doc.descendants((node, pos) => {
            if (!node.isText) return;

            const marks = node.marks.filter(
              (mark) =>
                mark.type.name === this.name &&
                mark.attrs.suggestionId === suggestionId,
            );

            marks.forEach((mark) => {
              found = true;
              if (dispatch) {
                tr.removeMark(pos, pos + node.nodeSize, mark);
              }
            });
          });

          if (found && dispatch) {
            dispatch(tr);
          }

          return found;
        },

      acceptSuggestion:
        (suggestionId: string) =>
        ({ tr, state, dispatch }) => {
          const { doc } = state;

          // Find the full mark range for the suggestion by ID.
          // The mark may span multiple text nodes (e.g., across line breaks),
          // so we need to find all nodes with this mark and compute the full range.
          let from: number | null = null;
          let to: number | null = null;

          doc.descendants((node, pos) => {
            if (!node.isText) return;

            const mark = node.marks.find(
              (m) =>
                m.type.name === this.name &&
                m.attrs.suggestionId === suggestionId,
            );

            if (mark) {
              // Track the earliest start position
              if (from === null || pos < from) {
                from = pos;
              }
              // Track the latest end position
              const nodeEnd = pos + node.nodeSize;
              if (to === null || nodeEnd > to) {
                to = nodeEnd;
              }
            }
          });

          if (from === null || to === null) return false;

          // Delete the text range (this also removes the mark)
          if (dispatch) {
            tr.delete(from, to);
            // A whole-section suggestion empties its paragraph (or list item),
            // which would serialize as an extra blank line. Remove the outermost
            // block the deletion left without text, keeping the doc non-empty.
            // `from` is still valid: the delete collapses the range onto it.
            const $pos = tr.doc.resolve(from);
            for (let depth = 1; depth <= $pos.depth; depth++) {
              // No text also covers a block holding only a hard break, which
              // still renders as a blank line.
              if ($pos.node(depth).textContent !== "") continue;
              if (depth > 1 || tr.doc.childCount > 1) {
                tr.delete($pos.before(depth), $pos.after(depth));
              }
              break;
            }
          }

          return true;
        },

      rejectSuggestion:
        (suggestionId: string) =>
        ({ commands }) => {
          // Reject is semantically the same as unsetting the mark
          return commands.unsetSuggestionHighlight(suggestionId);
        },
    };
  },
});
